<template>
  <transition name="fade" :duration="{ enter: 600, leave: 300 }">
    <div class="chatbox-viewport">
      <VueDraggableResizable
        v-if="isVisible || forcePopup"
        :w="width"
        :h="height"
        :x="position.x"
        :y="position.y"
        :draggable="true"
        drag-handle=".dialog-header"
        drag-cancel=".action-icon, .close-btn, button, img"
        :resizable="true"
        :parent="true"
        :minWidth="minWidth"
        :minHeight="minHeight"
        :maxWidth="maxDialogWidth"
        :maxHeight="maxDialogHeight"
        style="position: absolute; z-index: 1000"
        @dragging="onDragging"
        @dragstop="onDragStop"
        @resizing="onResizing"
        @resizestop="onResizeStop"
      >
        <div class="dialog-container">
          <div class="dialog">
            <ChatHeader
              :title="strategy.getStrategyTitle()"
              :description="chatDescription"
              @close="closeChatBox"
              @clear="clearHistory"
            />

            <ChatBody
              :messages="messagesFromStore"
              :style="{ opacity: showLoading ? '0.3' : '1' }"
              @feedback="handleFeedback"
            />

            <SpinningWheel v-if="showLoading && !hasReasoningSteps" />
            <LoadingBar
              v-if="showLoading"
              :progressValue="loadingProgress"
              :message="loadingMessage"
            />

            <ChatFooter
              @send="addMessage"
              @regenerate="regenerateSummary"
              :showRegenerate="true"
              :disableRegenerate="this.showLoading"
            />
          </div>
        </div>
      </VueDraggableResizable>
    </div>
  </transition>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import VueDraggableResizable from 'vue-draggable-resizable';
import { v4 as uuidv4 } from 'uuid';
import ChatHeader from './components/ChatHeader';
import ChatBody from './components/ChatBody';
import ChatFooter from './components/ChatFooter';
import SpinningWheel from './components/SpinningWheel';
import LoadingBar from './components/LoadingBar';

import amplifyEventService from '../../services/aws-amplify/amplifyEvent';
import onlineService from '../../services/onlineService';
import { SEND_REPAIR_SELECT_REQUEST } from '../../store/repairselect/actions/actionTypes';
import { REPAIR_SELECT_REQUEST } from '../../store/jobs/actions/actionTypes';

export default {
  components: {
    ChatHeader,
    ChatBody,
    ChatFooter,
    VueDraggableResizable,
    SpinningWheel,
    LoadingBar,
  },
  props: {
    isVisible: Boolean,
    title: String,
    message: String,
    chatboxMetadata: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    strategy: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      position: {
        x: Math.max(0, window.innerWidth - 680 - 200),
        y: Math.max(0, window.innerHeight - 520 - 200),
      },
      width: 680,
      height: 520,
      minWidth: 360,
      minHeight: 420,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      showLoading: false,
      loadingProgress: 0,
      loadingMessage: this.$t('chat_box.returning_answer'),
      reasoningSteps: [],
      channel: null,
      connectingChannel: false,
      chatId: uuidv4(),
      chatPreviousMessage: null,
      forcePopup: false,
      summaryAlreadyRequested: false,
    };
  },

  watch: {
    $props: {
      deep: true,
      immediate: true,
      handler({ strategy }) {
        if (!strategy || typeof strategy.getIdentity !== 'function') return;

        if (this.isVisible) {
          this.handleVisibilityChange();
        }
      },
    },

    message(newMessage) {
      if (!this.sessionKey) return;

      const initialMessage = { text: newMessage, isUser: false };

      const messagesGetter = this.$store.getters['ChatModule/messages'];

      const existingMessages =
        typeof messagesGetter === 'function'
          ? messagesGetter(this.sessionKey)
          : [];

      if (!existingMessages.length) {
        this.$store.dispatch('ChatModule/appendMessage', {
          sessionKey: this.sessionKey,
          message: initialMessage,
        });
      }
    },

    sessionKey: {
      immediate: true,
      handler(newKey) {
        if (!newKey) return;

        this.$store.dispatch('ChatModule/initSession', newKey);
      },
    },
  },

  async mounted() {
    this.$store.dispatch('ChatModule/cleanOldSessions', 30);

    this.initializeDialogPosition();

    if (this.isVisible || this.forcePopup) {
      await this.ensureChannelSubscription();
    }

    window.addEventListener('resize', this.handleWindowResize);
  },
  beforeDestroy() {
    this.persistDialogState();
    window.removeEventListener('resize', this.handleWindowResize);

    if (this.channel && typeof this.channel.close === 'function') {
      this.channel.close();
    }
  },

  computed: {
    chatDescription() {
      return this.strategy?.getStrategyDescription
        ? this.strategy.getStrategyDescription(this.chatboxMetadata)
        : '';
    },

    sessionKey() {
      if (!this.strategy) return null;

      const strategyType = this.strategy.type || 'unknown';
      const entityId = this.resolveEntityId(this.chatboxMetadata);

      const rawIdentity =
        (this.strategy.getIdentity &&
          this.strategy.getIdentity(this.chatboxMetadata)) ||
        'default';

      const identityId = String(rawIdentity);

      return `${strategyType}:${entityId}-${identityId}`;
    },

    messagesFromStore() {
      if (!this.sessionKey) return [];

      const getter = this.$store.getters['ChatModule/messages'];
      return typeof getter === 'function' ? getter(this.sessionKey) : [];
    },

    hasReasoningSteps() {
      return this.reasoningSteps.length > 0;
    },

    interactionsFromStore() {
      if (!this.sessionKey) return [];

      const getter = this.$store.getters['ChatModule/interactions'];
      return typeof getter === 'function' ? getter(this.sessionKey) : [];
    },
    maxDialogWidth() {
      return Math.max(this.minWidth, this.viewport.width);
    },
    maxDialogHeight() {
      return Math.max(this.minHeight, this.viewport.height);
    },
  },

  methods: {
    async ensureChannelSubscription() {
      if (this.channel || this.connectingChannel) {
        return;
      }

      this.connectingChannel = true;
      try {
        const isOnline = await onlineService.canConnectRealtime();
        if (!isOnline) {
          return;
        }

        this.channel = await amplifyEventService.connectToChannel('/dij/*');
        if (this.channel) {
          this.setupChannelSubscription();
        }
      } finally {
        this.connectingChannel = false;
      }
    },

    resetChatBox() {
      if (!this.sessionKey) return;

      const initialMessage = { text: this.message, isUser: false };

      const messagesGetter = this.$store.getters['ChatModule/messages'];
      const existingMessages =
        typeof messagesGetter === 'function'
          ? messagesGetter(this.sessionKey)
          : [];

      if (!existingMessages.length) {
        this.$store.dispatch('ChatModule/appendMessage', {
          sessionKey: this.sessionKey,
          message: initialMessage,
        });
      }

      this.chatPreviousMessage = null;
      this.summaryAlreadyRequested = false;
      this.chatId = uuidv4();
      this.initializeDialogPosition();

      const shouldAutoSubmit = this.strategy?.shouldAutoSubmit?.() ?? true;
      if (shouldAutoSubmit) {
        this.sendRequest();
      }
    },

    regenerateSummary() {
      this.sendRequest(true);
    },
    setupChannelSubscription() {
      this.channel.subscribe({
        next: (data) => {
          let { event } = data;
          if (typeof event === 'string') {
            try {
              event = JSON.parse(event);
            } catch (e) {
              console.error('Failed to parse event', e);
            }
          }

          if (event.provided_id === this.chatId) {
            this.loadingProgress = event.progress_level;
            if (event.message) {
              this.loadingMessage = event.message;
              // Accumulate reasoning steps with elapsed time
              const now = Date.now();
              if (this.reasoningSteps.length > 0) {
                const prev =
                  this.reasoningSteps[this.reasoningSteps.length - 1];
                this.$set(prev, 'elapsed', this.formatElapsed(now - prev._ts));
              }
              this.reasoningSteps.push({
                message: event.message,
                _ts: now,
                elapsed: null,
              });
            }

            if (event.result || event.error) {
              setTimeout(() => {
                const result = this.strategy.onMessageReceived(event);

                if (result.isError) {
                  this.addSystemMessage(result.text);
                  this.toggleLoading(false);
                } else {
                  if (!this.isVisible) {
                    this.$buefy.toast.open({
                      duration: 5000,
                      message: this.$t('chat_box.summary_generated'),
                      type: 'is-success',
                      position: 'is-bottom',
                    });
                    this.forcePopup = true;
                  }

                  const replyMessage = {
                    text: result.text || '',
                    copyText: result.copyText,
                    documents: result.documents || [],
                    isUser: false,
                    canBeCopied: true,
                  };

                  if (this.sessionKey) {
                    this.$store.dispatch('ChatModule/appendMessage', {
                      sessionKey: this.sessionKey,
                      message: replyMessage,
                    });

                    if (result.documents && result.documents.length) {
                      this.$store.dispatch('ChatModule/appendDocuments', {
                        sessionKey: this.sessionKey,
                        documents: result.documents,
                      });
                    }
                  }

                  this.chatPreviousMessage = result.text;
                  this.toggleLoading(false);
                }
                this.loadingProgress = 0;
              }, 1500);
            }
          }
        },
        error: (err) => console.error('Subscription error:', err),
      });
    },
    toggleLoading(state) {
      this.showLoading = state;
    },
    formatElapsed(ms) {
      if (ms < 1000) return `${ms}ms`;
      return `${(ms / 1000).toFixed(1)}s`;
    },
    getCurrentTimestamp() {
      const now = new Date();
      return `${now.getHours().toString().padStart(2, '0')}h:${now
        .getMinutes()
        .toString()
        .padStart(2, '0')}m:${now.getSeconds().toString().padStart(2, '0')}s`;
    },
    createInteraction(content = 'no user interaction content') {
      const sanitizeString = (value) =>
        typeof value === 'string' ? value.replace(/'/g, '') : value;

      const currentInteractions = this.sessionKey
        ? this.interactionsFromStore
        : [];

      return {
        interactionId: currentInteractions.length + 1,
        timestamp: this.getCurrentTimestamp(),
        reply: {
          type: 'reply type',
          content: sanitizeString(this.chatPreviousMessage) || '',
        },
        userInteraction: {
          type: 'user interaction type',
          content,
        },
      };
    },

    createPayload(forceRegenerate) {
      if (forceRegenerate && this.sessionKey) {
        this.$store.dispatch('ChatModule/replaceInteractions', {
          sessionKey: this.sessionKey,
          interactions: [],
        });
      }

      const interactions =
        this.sessionKey && this.interactionsFromStore.length
          ? this.interactionsFromStore
          : [];

      return this.strategy.createPayload(
        this.chatId,
        this.chatboxMetadata,
        interactions
      );
    },

    async handleVisibilityChange() {
      await this.ensureChannelSubscription();

      this.initializeDialogPosition();

      const currentStrategyType = this.strategy?.type ?? null;
      const currentEntityIdentifier =
        this.strategy?.getIdentity(this.chatboxMetadata) ?? null;
      const currentEntityId = this.resolveEntityId(this.chatboxMetadata);

      const storedSettings = this.getStoredStrategySettings();
      const storedStrategyType = storedSettings?.strategyType ?? null;
      const storedEntityIdentifier = storedSettings?.entityIdentifier ?? null;
      const storedEntityId = storedSettings?.entityId ?? null;

      const strategyChanged = currentStrategyType !== storedStrategyType;
      const identityChanged =
        currentEntityIdentifier !== storedEntityIdentifier;
      const entityChanged = currentEntityId !== storedEntityId;

      if (strategyChanged || identityChanged || entityChanged) {
        this.resetChatBox();
        this.saveStrategySettings(
          currentStrategyType,
          currentEntityIdentifier,
          currentEntityId
        );
        return;
      }

      const shouldAutoSubmit = this.strategy?.shouldAutoSubmit?.() ?? true;
      const hasHistory = (this.messagesFromStore || []).length > 1;
      if (!hasHistory && !this.summaryAlreadyRequested && shouldAutoSubmit) {
        await this.sendRequest();
      }
    },

    getStoredStrategySettings() {
      try {
        const raw = localStorage.getItem('chatBoxStrategy');
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        console.warn('Failed to parse stored strategy settings', e);
        return null;
      }
    },

    saveStrategySettings(type, identifier, entityId) {
      const settings = {
        strategyType: type,
        entityIdentifier: identifier,
        entityId,
      };
      localStorage.setItem('chatBoxStrategy', JSON.stringify(settings));
    },

    resolveEntityId(metadata) {
      if (!metadata || typeof metadata !== 'object') return 'unknown';

      return (
        metadata._id ||
        metadata.id ||
        metadata.jobId ||
        metadata.workOrderNumber ||
        metadata.workOrderNo ||
        metadata.workOrderName?.[0]?.text ||
        metadata.serialNumber ||
        'unknown'
      );
    },

    async sendRequest(forceRegenerate) {
      try {
        const payload = this.createPayload(forceRegenerate);

        this.summaryAlreadyRequested = true;
        this.toggleLoading(true);
        this.loadingProgress = 0.05;
        this.loadingMessage = this.$t('chat_box.returning_answer');
        this.reasoningSteps = [];

        await this.$store.dispatch(SEND_REPAIR_SELECT_REQUEST, payload);
        await this.$store.dispatch(REPAIR_SELECT_REQUEST, this.chatboxMetadata);
      } catch (error) {
        this.toggleLoading(false);
        console.error('Error sending message:', error);
        this.addSystemMessage('An error occurred while processing the data');
      }
    },
    closeChatBox() {
      this.persistDialogState();
      this.$emit('close');
      this.forcePopup = false;
    },
    async handleFeedback(sentiment) {
      try {
        const payload = {
          requestType: 'feedback',
          providedId: this.chatId,
          sentiment,
          customer: '',
          serialNumber: '',
          jobId: '',
          interactionType: this.strategy?.type || '',
          appLocation: '',
          model: [],
          oem: [],
          ratData: [],
          interactionData: [],
        };
        await this.$store.dispatch(SEND_REPAIR_SELECT_REQUEST, payload);
        this.$buefy.toast.open({
          duration: 3000,
          message: this.$t('chat_box.feedback_thanks'),
          type: 'is-success',
          position: 'is-bottom',
        });
      } catch (error) {
        console.error('Error sending feedback:', error);
        this.$buefy.toast.open({
          duration: 5000,
          message: this.$t('chat_box.feedback_error'),
          type: 'is-danger',
          position: 'is-bottom',
        });
      }
    },
    clearHistory() {
      this.$store.dispatch('ChatModule/resetSession', this.sessionKey);
      this.resetChatBox();
    },
    initializeDialogPosition() {
      const defaultState = this.getDefaultDialogState();
      let nextState = defaultState;

      try {
        const saved = localStorage.getItem('chatBoxDialogState');
        if (saved) {
          const parsed = JSON.parse(saved);
          nextState = {
            position: {
              x: parsed?.position?.x,
              y: parsed?.position?.y,
            },
            width: parsed?.width,
            height: parsed?.height,
          };
        }
      } catch (error) {
        nextState = defaultState;
      }

      const width = Number(nextState.width);
      const height = Number(nextState.height);
      const posX = Number(nextState.position?.x);
      const posY = Number(nextState.position?.y);

      this.width = Number.isFinite(width) ? width : defaultState.width;
      this.height = Number.isFinite(height) ? height : defaultState.height;
      this.position = {
        x: Number.isFinite(posX) ? posX : defaultState.position.x,
        y: Number.isFinite(posY) ? posY : defaultState.position.y,
      };

      this.applyBounds();
      this.persistDialogState();
    },
    getDefaultDialogState() {
      const margin = 200;
      const defaultWidth = 680;
      const defaultHeight = 520;

      const width = Math.max(
        this.minWidth,
        Math.min(defaultWidth, this.viewport.width)
      );
      const height = Math.max(
        this.minHeight,
        Math.min(defaultHeight, this.viewport.height)
      );

      return {
        width,
        height,
        position: {
          x: Math.max(0, this.viewport.width - width - margin),
          y: Math.max(0, this.viewport.height - height - margin),
        },
      };
    },
    applyBounds() {
      const boundedSize = this.clampSize(this.width, this.height);
      this.width = boundedSize.width;
      this.height = boundedSize.height;

      this.position = this.clampPosition(
        this.position.x,
        this.position.y,
        this.width,
        this.height
      );
    },
    clampSize(width, height) {
      const safeWidth = Math.min(
        Math.max(width, this.minWidth),
        this.maxDialogWidth
      );
      const safeHeight = Math.min(
        Math.max(height, this.minHeight),
        this.maxDialogHeight
      );

      return { width: safeWidth, height: safeHeight };
    },
    clampPosition(left, top, width, height) {
      const maxX = Math.max(0, this.viewport.width - width);
      const maxY = Math.max(0, this.viewport.height - height);

      return {
        x: Math.min(Math.max(left, 0), maxX),
        y: Math.min(Math.max(top, 0), maxY),
      };
    },
    handleWindowResize() {
      this.viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      this.applyBounds();
      this.persistDialogState();
    },
    getDragPosition(arg1, arg2) {
      if (typeof arg1 === 'number' && typeof arg2 === 'number') {
        return { x: arg1, y: arg2 };
      }

      if (arg1 && typeof arg1 === 'object') {
        const x = Number.isFinite(Number(arg1.x))
          ? Number(arg1.x)
          : Number(arg1.left);
        const y = Number.isFinite(Number(arg1.y))
          ? Number(arg1.y)
          : Number(arg1.top);

        if (Number.isFinite(x) && Number.isFinite(y)) {
          return { x, y };
        }
      }

      return null;
    },
    onDragging(arg1, arg2) {
      const point = this.getDragPosition(arg1, arg2);
      if (!point) return;

      this.position = this.clampPosition(
        point.x,
        point.y,
        this.width,
        this.height
      );
    },
    onDragStop(arg1, arg2) {
      const point = this.getDragPosition(arg1, arg2);
      if (point) {
        this.position = this.clampPosition(
          point.x,
          point.y,
          this.width,
          this.height
        );
      }
      this.persistDialogState();
    },
    getResizePayload(arg1, arg2, arg3, arg4) {
      if (
        [arg1, arg2, arg3, arg4].every((value) =>
          Number.isFinite(Number(value))
        )
      ) {
        return {
          left: Number(arg1),
          top: Number(arg2),
          width: Number(arg3),
          height: Number(arg4),
        };
      }

      if (arg1 && typeof arg1 === 'object') {
        const left = Number.isFinite(Number(arg1.x))
          ? Number(arg1.x)
          : Number(arg1.left);
        const top = Number.isFinite(Number(arg1.y))
          ? Number(arg1.y)
          : Number(arg1.top);
        const width = Number.isFinite(Number(arg1.width))
          ? Number(arg1.width)
          : Number(arg1.w);
        const height = Number.isFinite(Number(arg1.height))
          ? Number(arg1.height)
          : Number(arg1.h);

        if (
          Number.isFinite(left) &&
          Number.isFinite(top) &&
          Number.isFinite(width) &&
          Number.isFinite(height)
        ) {
          return { left, top, width, height };
        }
      }

      return null;
    },
    onResizing(arg1, arg2, arg3, arg4) {
      const payload = this.getResizePayload(arg1, arg2, arg3, arg4);
      if (!payload) return;

      const boundedSize = this.clampSize(payload.width, payload.height);
      this.width = boundedSize.width;
      this.height = boundedSize.height;
      this.position = this.clampPosition(
        payload.left,
        payload.top,
        this.width,
        this.height
      );
    },
    onResizeStop(arg1, arg2, arg3, arg4) {
      const payload = this.getResizePayload(arg1, arg2, arg3, arg4);
      if (payload) {
        const boundedSize = this.clampSize(payload.width, payload.height);
        this.width = boundedSize.width;
        this.height = boundedSize.height;
        this.position = this.clampPosition(
          payload.left,
          payload.top,
          this.width,
          this.height
        );
      }
      this.persistDialogState();
    },
    persistDialogState() {
      try {
        const state = {
          position: this.position,
          width: this.width,
          height: this.height,
        };
        localStorage.setItem('chatBoxDialogState', JSON.stringify(state));
      } catch (error) {
        // Ignore storage access errors
      }
    },
    addMessage(newMessage) {
      if (!this.sessionKey) return;

      const userMessage = { text: newMessage, isUser: true };
      const interaction = this.createInteraction(newMessage);

      this.$store.dispatch('ChatModule/appendMessage', {
        sessionKey: this.sessionKey,
        message: userMessage,
      });
      this.$store.dispatch('ChatModule/appendInteraction', {
        sessionKey: this.sessionKey,
        interaction,
      });

      this.parseMessage(newMessage);
    },

    addSystemMessage(text) {
      if (!this.sessionKey || !text) return;

      const msg = { text, isUser: false };

      this.$store.dispatch('ChatModule/appendMessage', {
        sessionKey: this.sessionKey,
        message: msg,
      });
    },

    parseMessage(message) {
      const systemMsg = this.strategy.parseUserMessage(message);
      this.addSystemMessage(systemMsg);
      setTimeout(() => this.sendRequest(), 1000);
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from {
  opacity: 0;
}
.fade-enter-to {
  opacity: 1;
}
.fade-leave-from {
  opacity: 1;
}
.fade-leave-to {
  opacity: 0;
}

.chatbox-viewport {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 1000;
}

.chatbox-viewport > .vdr {
  pointer-events: auto;
}

.dialog-container {
  width: 100%;
  height: 100%;
}

.dialog {
  width: 100%;
  height: 100%;
  background-color: #f7f7f7;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #ccc;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  pointer-events: auto;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18), 0 0 10px rgba(91, 62, 165, 0.3);
  border-radius: 12px;
}

.dialog ::-webkit-scrollbar {
  width: 6px;
}

.dialog ::-webkit-scrollbar-thumb {
  background-color: #5b3ea5;
  border-radius: 3px;
}

.fade-enter-active {
  transition: opacity 0.6s ease;
}

.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  transform: translate3d(0, 0, 0) scale(0.98);
}
.fade-enter-to,
.fade-leave-from {
  transform: translate3d(0, 0, 0) scale(1);
}
</style>
